import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { GitBranch, ListChecks, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Select } from '@/components/select';
import { useNodeStore } from '@/stores/node-store';
import type { InputValue, NodeStruct } from '@/types/node-types';
import { getSwitchConfig } from '@/pages/workflow/helper/functional-control-flow';
import {
  CLASSIFY_LABEL_RE,
  PRESET_CARDS,
  describeCaseSync,
  describeClassifyPlan,
  findDownstreamSwitches,
  findLoopsJudgedBy,
  getClassifyConfig,
  normalizeLabel,
  planCaseSync,
  presetForLabels,
  validateClassifyConfig,
  type PresetId,
} from '@/pages/workflow/helper/classify-decide';
import { sentenceFromGraph } from '@/pages/workflow/helper/repeat-author';
import { getDecisionPresetUpdates } from './classify-decision-presets';
import { LabelChips } from './label-chips';
import { cn } from '@/lib/utils';

interface ClassifyDecidePanelProps {
  nodeId: string;
  struct: NodeStruct;
  onChange: (fieldId: string, value: InputValue) => void;
  disabled?: boolean;
}

const hint = 'text-xs text-muted-foreground';

/**
 * Classify / Decide as a form: pick a preset or name your own labels, say
 * how to decide, choose a fallback for low confidence, and keep the Switch
 * or loop downstream in step with the labels, each with an explicit button.
 */
export function ClassifyDecidePanel({
  nodeId,
  struct,
  onChange,
  disabled = false,
}: ClassifyDecidePanelProps) {
  const {
    nodes,
    edges,
    groupedNodes,
    handleParameterChange,
    applyLoopSentence,
  } = useNodeStore(
    useShallow((state) => ({
      nodes: state.nodes,
      edges: state.edges,
      groupedNodes: state.groupedNodes,
      handleParameterChange: state.handleParameterChange,
      applyLoopSentence: state.applyLoopSentence,
    })),
  );
  const catalog = useMemo(
    () => Object.values(groupedNodes).flat(),
    [groupedNodes],
  );
  const config = useMemo(() => getClassifyConfig(struct), [struct]);
  const problems = validateClassifyConfig(config);
  const activePreset: PresetId =
    config.preset === 'custom' ? presetForLabels(config.labels) : config.preset;
  const invalidLabels = useMemo(() => {
    const seen = new Set<string>();
    const bad = new Set<string>();
    for (const label of config.labels) {
      if (!CLASSIFY_LABEL_RE.test(label) || seen.has(label)) bad.add(label);
      seen.add(label);
    }
    return bad;
  }, [config.labels]);

  const switches = useMemo(
    () => findDownstreamSwitches(nodes, edges, nodeId),
    [nodes, edges, nodeId],
  );
  const loops = useMemo(
    () => findLoopsJudgedBy(nodes, edges, nodeId),
    [nodes, edges, nodeId],
  );
  const [loopDoneLabel, setLoopDoneLabel] = useState<string | undefined>(
    undefined,
  );

  const pickPreset = (preset: PresetId) => {
    onChange('decision_preset', preset);
    if (preset === 'custom') return;
    for (const update of getDecisionPresetUpdates(preset, {
      decisionLabels: config.labels,
      criteria: config.criteria,
    })) {
      onChange(update.fieldId, update.value);
    }
  };

  const setLabels = (labels: string[]) => {
    onChange('decision_labels', labels);
    // A hand-edited set is no longer the preset it started from.
    if (
      config.preset !== 'custom' &&
      presetForLabels(labels) !== config.preset
    ) {
      onChange('decision_preset', 'custom');
    }
    if (config.uncertainLabel && !labels.includes(config.uncertainLabel)) {
      onChange('uncertain_label', '');
    }
  };

  const syncSwitch = (switchId: string) => {
    const target = nodes.find((node) => node.id === switchId);
    const entry = switches.find((item) => item.switchId === switchId);
    if (!target || !entry) return;
    const plan = planCaseSync(
      getSwitchConfig(target.data.struct).cases,
      config.labels,
      entry.decisionPath,
    );
    handleParameterChange(
      switchId,
      'cases',
      plan.cases as unknown as InputValue,
    );
  };

  const syncLoop = (carrierId: string) => {
    const label = loopDoneLabel ?? config.labels[0];
    if (!label) return;
    const { sentence } = sentenceFromGraph(nodes, edges, carrierId, catalog);
    applyLoopSentence(carrierId, {
      ...sentence,
      judgeId: nodeId,
      condition: {
        kind: 'rule',
        field: ['decision'],
        op: 'eq',
        value: label,
        guardMissing: false,
      },
      feedbackPath: ['result'],
    });
  };

  const threshold = config.uncertainThreshold ?? 0.6;
  const problemFor = (field: 'labels' | 'criteria' | 'uncertain') =>
    problems.find((problem) => problem.field === field)?.message ?? null;

  return (
    <div className='space-y-4 px-3 py-2' data-testid='classify-panel'>
      <p className={hint} data-testid='classify-plan'>
        {describeClassifyPlan(config)}
      </p>

      {/* Presets */}
      <section className='space-y-2'>
        <Label className='text-sm'>What kind of decision?</Label>
        <div className='grid grid-cols-1 gap-2 sm:grid-cols-3'>
          {PRESET_CARDS.map((card) => (
            <button
              key={card.id}
              type='button'
              disabled={disabled}
              data-testid={`classify-preset-${card.id}`}
              aria-pressed={activePreset === card.id}
              className={cn(
                'rounded-md border p-2 text-left text-xs transition-colors',
                activePreset === card.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                  : 'border-border hover:border-blue-300',
                disabled && 'cursor-default opacity-70',
              )}
              onClick={() => pickPreset(card.id)}
            >
              <div className='font-medium'>{card.title}</div>
              <div className='text-muted-foreground'>{card.summary}</div>
              {card.labels.length ? (
                <div className='mt-1 font-mono text-[10px] text-muted-foreground'>
                  {card.labels.join(' · ')}
                </div>
              ) : null}
            </button>
          ))}
        </div>
      </section>

      {/* Labels */}
      <section className='space-y-1.5'>
        <div className='flex items-center justify-between'>
          <Label className='text-sm'>Labels</Label>
          <span className={hint}>{config.labels.length} of 16</span>
        </div>
        <LabelChips
          labels={config.labels}
          invalid={invalidLabels}
          highlighted={config.uncertainLabel}
          disabled={disabled}
          normalize={normalizeLabel}
          onChange={setLabels}
          testIdPrefix='classify-label'
        />
        {problemFor('labels') ? (
          <p
            className='text-xs text-red-500'
            data-testid='classify-labels-problem'
          >
            {problemFor('labels')}
          </p>
        ) : (
          <p className={hint}>
            One of these becomes the answer. Click a label to rename it.
          </p>
        )}
      </section>

      {/* Criteria */}
      <section className='space-y-1.5'>
        <Label className='text-sm'>How to decide</Label>
        <Textarea
          value={config.criteria}
          disabled={disabled}
          rows={5}
          data-testid='classify-criteria'
          placeholder='Say what makes the subject each label, in plain words.'
          className='text-xs'
          onChange={(event) => onChange('criteria', event.target.value)}
        />
        {problemFor('criteria') ? (
          <p className='text-xs text-red-500'>{problemFor('criteria')}</p>
        ) : null}
      </section>

      {/* Fallback */}
      <section className='space-y-1.5'>
        <Label className='text-sm'>When unsure</Label>
        <div className='flex flex-wrap items-center gap-2 text-xs'>
          <span>If confidence is below</span>
          <span className='font-mono'>{Math.round(threshold * 100)}%</span>
          <span>answer</span>
          <Select
            options={['(no fallback)', ...config.labels]}
            selected={config.uncertainLabel ?? '(no fallback)'}
            allowAllOption={false}
            disabled={disabled}
            className='h-7 w-auto min-w-[120px] text-xs'
            onSelect={(value) => {
              const next = !value || value === '(no fallback)' ? '' : value;
              onChange('uncertain_label', next);
              if (next && config.uncertainThreshold === null) {
                onChange('uncertain_threshold', threshold);
              }
            }}
          />
        </div>
        {config.uncertainLabel ? (
          <Slider
            value={[threshold]}
            min={0}
            max={1}
            step={0.05}
            disabled={disabled}
            aria-label='Confidence threshold'
            onValueChange={([value]) => onChange('uncertain_threshold', value)}
          />
        ) : null}
        {problemFor('uncertain') ? (
          <p className='text-xs text-red-500'>{problemFor('uncertain')}</p>
        ) : (
          <p className={hint}>
            A fallback label lets a Switch send doubtful cases to a person
            instead of guessing.
          </p>
        )}
      </section>

      {/* Downstream */}
      {switches.length || loops.length ? (
        <section
          className='space-y-2 rounded-md border bg-muted/20 p-3'
          data-testid='classify-downstream'
        >
          <div className='text-xs font-semibold text-muted-foreground'>
            Uses these labels
          </div>
          {switches.map((item) => {
            const target = nodes.find((node) => node.id === item.switchId);
            const plan = planCaseSync(
              target ? getSwitchConfig(target.data.struct).cases : [],
              config.labels,
              item.decisionPath,
            );
            const inSync = plan.added.length === 0 && plan.removed.length === 0;
            return (
              <div
                key={item.switchId}
                className='flex flex-wrap items-center justify-between gap-2 text-xs'
                data-testid={`classify-sync-switch-${item.switchId}`}
              >
                <div className='flex min-w-0 items-center gap-1.5'>
                  <GitBranch className='size-3.5 shrink-0' />
                  <span className='truncate'>
                    <span className='font-medium'>{item.switchName}</span>{' '}
                    {inSync
                      ? 'routes on these labels.'
                      : describeCaseSync(plan)}
                  </span>
                </div>
                {!inSync ? (
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    className='h-7 text-xs'
                    disabled={disabled || config.labels.length === 0}
                    data-testid={`classify-sync-switch-button-${item.switchId}`}
                    onClick={() => syncSwitch(item.switchId)}
                  >
                    Update its cases
                  </Button>
                ) : null}
              </div>
            );
          })}
          {loops.map((loop) => (
            <div
              key={loop.carrierId}
              className='flex flex-wrap items-center justify-between gap-2 text-xs'
              data-testid={`classify-sync-loop-${loop.carrierId}`}
            >
              <div className='flex min-w-0 flex-wrap items-center gap-1.5'>
                <RotateCcw className='size-3.5 shrink-0' />
                <span className='font-medium'>{loop.carrierName}</span>
                <span>stops when the decision is</span>
                <Select
                  options={config.labels}
                  selected={loopDoneLabel ?? config.labels[0]}
                  allowAllOption={false}
                  disabled={disabled}
                  className='h-7 w-auto min-w-[110px] text-xs'
                  onSelect={(value) => setLoopDoneLabel(value)}
                />
              </div>
              <Button
                type='button'
                size='sm'
                variant='outline'
                className='h-7 text-xs'
                disabled={disabled || config.labels.length === 0}
                data-testid={`classify-sync-loop-button-${loop.carrierId}`}
                onClick={() => syncLoop(loop.carrierId)}
              >
                Update the loop condition
              </Button>
            </div>
          ))}
        </section>
      ) : (
        <p className={cn(hint, 'flex items-center gap-1.5')}>
          <ListChecks className='size-3.5' />
          Connect the Decision output to a Switch, or make this the judge of a
          Repeat Until, and the labels can be kept in step from here.
        </p>
      )}
    </div>
  );
}
